/** Площадки «Аналитики МП» — ЕДИНСТВЕННОЕ место, где Ozon и WB различаются в UI: вкладки,
 *  карточки, пресеты дерева, ссылка на товар, подписи. Остальной код берёт конфиг площадки
 *  (`MP_CONFIGS[mp]`, внутри страницы — `useMp()`), а не проверяет `mp === "wb"`.
 *
 *  Площадка — параметр адреса `mp` (state.ts): `mp=wb` — WB, нет — Ozon. */
import { createContext, useContext } from "react";

import { MP_IDS, type Mp } from "@/api/mpAnalytics";
import { mpProductUrl } from "@/lib/marketplace";
import type { CardKind, Tab } from "./state";

export { MP_IDS, type Mp };

export interface TreePreset {
  id: string;
  label: string;
  levels: string[];
}

/** Блок «география» карточки артикула: разбивка по измерению `dim`, строка без значения
 *  (ключ '') — последней, вне топа. */
export interface GeoBlock {
  dim: string;
  title: string;
  col: string;
  noneLabel: string;
  noneMarginTitle: string;
  what: string;
  noun: string;
}

export interface MpConfig {
  id: Mp;
  /** короткое имя площадки в подписях: «Комиссия Ozon», «Открыть на WB» */
  label: string;
  /** вкладки площадки по порядку (умолчание — summary; plan — общая для площадок, первой) */
  tabs: Tab[];
  /** карточки сущности площадки (боковая панель) */
  cards: CardKind[];
  /** пресеты дерева (коды — из `meta.dims` площадки); первый — по умолчанию */
  treePresets: TreePreset[];
  /** страница товара на площадке по `mp_sku`; нет `mp_sku` — ссылки нет */
  productUrl: (mpSku: string | null) => string | null;
  /** подпись товара площадки в карточке («SKU» у Ozon, «nmID» у WB) */
  skuLabel: string;
  /** пометка в шапке страницы (WB: суммы с НДС); null — нет */
  vatNote: string | null;
  /** витрина отдаёт свежесть по источникам (`mart.data_through`): нет её — предупреждение */
  freshnessBySource: boolean;
  searchPlaceholder: string;
  geo: GeoBlock;
  kpiHints: { revenue: string; qty: string };
  /** у площадки есть себестоимость (контракт mp_finance v1.4, Ozon — v1.7): плашка покрытия; нет —
   *  себестоимость, «Расходы» и «Маржа» в KPI, P&L и дереве «—» с подсказкой «для <площадки> нет» */
  cogs: boolean;
}

export const MP_CONFIGS: Record<Mp, MpConfig> = {
  ozon: {
    id: "ozon",
    label: "Ozon",
    tabs: ["plan", "summary", "daily", "tree", "supplies", "recon"],
    cards: ["article", "supply", "campaign"],
    treePresets: [
      { id: "items", label: "Статьи", levels: ["grp", "item", "cluster_from", "article", "obj"] },
      {
        id: "articles",
        label: "Артикулы",
        levels: ["category", "article", "grp", "item", "cluster_from"],
      },
      { id: "clusters", label: "Кластеры", levels: ["cluster_from", "grp", "item", "article"] },
      { id: "warehouses", label: "Склады", levels: ["warehouse", "grp", "item", "article"] },
      { id: "supplies", label: "Поставки", levels: ["grp", "item", "obj", "article"] },
    ],
    productUrl: (mpSku) => mpProductUrl("ozon", mpSku),
    skuLabel: "SKU",
    vatNote: null,
    freshnessBySource: false,
    searchPlaceholder: "Поиск: артикул, название, SKU, отправление, поставка, статья",
    geo: {
      dim: "cluster_to",
      title: "По кластерам доставки",
      col: "Кластер",
      noneLabel: "Без кластера доставки (хранение, поставки и др.)",
      noneMarginTitle:
        "Маржа % не считается: сюда падают хранение, поставки, реклама и прочие расходы без кластера доставки — их выручка в других строках",
      what: "разбивку по кластерам",
      noun: "кластеров",
    },
    kpiHints: {
      revenue: "с баллами Ozon и партнёрскими, за вычетом возвратов",
      qty: "«Оплачено покупателем» за вычетом сторно и возвратов",
    },
    cogs: true,
  },
  wb: {
    id: "wb",
    label: "WB",
    tabs: ["plan", "summary", "daily", "tree", "recon"],
    cards: ["article"],
    treePresets: [
      { id: "items", label: "Статьи", levels: ["grp", "item", "federal_district", "article", "obj"] },
      { id: "articles", label: "Артикулы", levels: ["category", "article", "grp", "item", "size"] },
      {
        id: "regions",
        label: "Регионы",
        levels: ["federal_district", "region", "grp", "item", "article"],
      },
      { id: "warehouses", label: "Склады", levels: ["warehouse", "grp", "item", "article"] },
      { id: "supplies", label: "Поставки", levels: ["grp", "item", "obj", "article"] },
    ],
    productUrl: (mpSku) => mpProductUrl("wb", mpSku),
    skuLabel: "nmID",
    vatNote: "суммы с НДС",
    freshnessBySource: true,
    searchPlaceholder: "Поиск: артикул, название, nmID, объект, статья",
    geo: {
      dim: "federal_district",
      title: "По федеральным округам",
      col: "Округ",
      noneLabel: "Без округа (хранение, реклама и др.)",
      noneMarginTitle:
        "Маржа % не считается: сюда падают хранение, реклама и прочие расходы без округа доставки — их выручка в других строках",
      what: "разбивку по округам",
      noun: "округов",
    },
    kpiHints: {
      revenue: "по цене продавца, за вычетом возвратов",
      qty: "продажи за вычетом возвратов",
    },
    cogs: true,
  },
};

export const MP_OPTIONS: { id: Mp; label: string }[] = MP_IDS.map((id) => ({
  id,
  label: MP_CONFIGS[id].label,
}));

/** Конфиг площадки страницы. Провайдер ставит OzonAnalyticsPage; без него — ошибка, а не Ozon
 *  «по умолчанию»: компонент вне страницы молча показал бы данные не той площадки. */
export const MpContext = createContext<MpConfig | null>(null);

export function useMp(): MpConfig {
  const v = useContext(MpContext);
  if (!v) throw new Error("MpContext не задан: компонент «Аналитики МП» вне страницы");
  return v;
}
